import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, X, RotateCcw, Play, Pause, ArrowRight, Check, DoorOpen, Hand, DollarSign, TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import PawMark from "@/components/PawMark";
import Avatar from "@/components/Avatar";
import { celebrateSigned } from "@/lib/celebrate";

/* ─── Persona directory (photos are optional — Avatar falls back to boutique initials) ─── */
const PERSONAS = {
  katrina: { name: "Katrina Gell", role: "COO", boutique: null, boutiqueLabel: "Rainbow Leadership", color: "#0F172A", accent: "bg-stone-900" },
  damon:   { name: "Damon Mitchell", role: "GM", boutique: "PLT", boutiqueLabel: "Plantation", color: "#6b8e5a", accent: "bg-emerald-700" },
  anastacia: { name: "Anastacia Young", role: "Massage Therapist", boutique: "PLT", boutiqueLabel: "Plantation", color: "#6b8e5a", accent: "bg-emerald-700" },
  sheila:  { name: "Sheila Wilkinson", role: "Onboarding Lead", boutique: null, boutiqueLabel: "Rainbow Leadership", color: "#0F172A", accent: "bg-stone-900" },
  rose:    { name: "Rose Chen", role: "Finance", boutique: null, boutiqueLabel: "Rainbow Leadership", color: "#0F172A", accent: "bg-stone-900" },
};

/* ─── The 6-shot script ─── */
const SHOTS = [
  {
    id: "s1",
    time: "7:00 AM",
    persona: "katrina",
    human: true,
    heading: "Four boutiques. One breath.",
    voice: "Four boutiques, one breath before the day starts. If PLT's short a therapist, I want to know before I finish this cup.",
    caption: "Katrina opens TOTO on her phone. Every boutique's morning is one glance away.",
    Preview: KatrinaPulsePreview,
  },
  {
    id: "s2",
    time: "9:00 AM",
    persona: "damon",
    human: false,
    heading: "Roster confirmed. Rooms ready. Two gratuities on deck.",
    voice: null,
    caption: "Damon lands on Team Plantation. Working roster, room resets, gratuities to route — all in one card.",
    Preview: DamonDailyPreview,
  },
  {
    id: "s3",
    time: "10:00 AM",
    persona: "anastacia",
    human: true,
    heading: "Small thing. Big feeling.",
    voice: "Damon just pushed a $40 gratuity from my 9:15. Small thing, but I feel seen. That's why I stay.",
    caption: "The gratuity Damon logged 60 minutes ago is now a green claim card on Anastacia's Home. TOTO connected them without either of them noticing.",
    Preview: AnastaciaHomePreview,
  },
  {
    id: "s4",
    time: "11:00 AM",
    persona: "sheila",
    human: false,
    heading: "Interview → Offer → Signed. 47 minutes.",
    voice: null,
    caption: "Sheila drags Kira Bennett to Conditional Offer. Template loads with real PLT address. Magic link sent. Signed at 11:47.",
    Preview: SheilaHiringPreview,
  },
  {
    id: "s5",
    time: "2:00 PM",
    persona: "rose",
    human: true,
    heading: "The fix beats the email.",
    voice: "Housekeeping at PLT just crossed 92% of monthly cap. TOTO already nudged Damon. The fix might happen before I finish this email.",
    caption: "Budget's amber bar surfaces on Rose's dashboard. The same signal has already appeared as a gentle nudge on Damon's Home. One data point, two people, zero forwarded emails.",
    Preview: RoseBudgetPreview,
  },
  {
    id: "s6",
    time: null,
    persona: null,
    human: null,
    heading: null,
    voice: null,
    caption: null,
    Preview: null,
    closing: true,
  },
];

/* ─── The Tour page ─── */
export default function Tour() {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);

  const shot = SHOTS[idx];
  const persona = shot.persona ? PERSONAS[shot.persona] : null;

  const goPrev = useCallback(() => setIdx((i) => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => setIdx((i) => Math.min(SHOTS.length - 1, i + 1)), []);
  const restart = useCallback(() => setIdx(0), []);

  // Keyboard nav
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); goNext(); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); }
      else if (e.key === "Escape") navigate("/");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev, navigate]);

  // Auto-play
  useEffect(() => {
    if (!autoPlay) return;
    if (idx === SHOTS.length - 1) { setAutoPlay(false); return; }
    const t = setTimeout(goNext, 6500);
    return () => clearTimeout(t);
  }, [autoPlay, idx, goNext]);

  // Fire confetti on the signed offer beat
  useEffect(() => {
    if (shot.id === "s4") setTimeout(() => celebrateSigned(), 500);
  }, [shot.id]);

  if (shot.closing) return <ClosingCard onRestart={restart} onEnter={() => navigate("/")} idx={idx} total={SHOTS.length} />;

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col" data-testid="tour-page" style={{ background: "var(--bg-main, #F7F4EE)" }}>
      {/* Top bar: paw · progress · controls */}
      <header className="flex items-center justify-between px-4 sm:px-10 py-5">
        <div className="flex items-center gap-2 text-stone-900">
          <PawMark size={16} overrideBoutique={persona?.boutique} />
          <span className="font-serif-display text-xl">TOTO</span>
          <span className="text-[10px] uppercase tracking-widest text-stone-400 ml-2">Guided Tour</span>
        </div>
        <div className="flex items-center gap-2">
          <ProgressDots total={SHOTS.length} idx={idx} onDot={setIdx} />
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAutoPlay((v) => !v)}
            data-testid="tour-autoplay"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] subtle-border text-stone-700 hover:bg-white"
          >
            {autoPlay ? <Pause size={12} /> : <Play size={12} />}
            {autoPlay ? "Pause" : "Auto"}
          </button>
          <button
            onClick={() => navigate("/")}
            data-testid="tour-exit"
            className="p-1.5 rounded-full text-stone-500 hover:bg-stone-100"
            aria-label="Exit tour"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>
      </header>

      {/* Main stage */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-10 pb-16">
        <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6 lg:gap-10 items-stretch">
          <NarrativePanel shot={shot} persona={persona} />
          <PreviewFrame shot={shot} persona={persona} />
        </div>
      </div>

      {/* Prev / next chevrons */}
      <button
        onClick={goPrev}
        disabled={idx === 0}
        data-testid="tour-prev"
        className="hidden lg:flex fixed left-6 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-white subtle-border shadow-sm disabled:opacity-30 disabled:cursor-not-allowed hover:bg-stone-50"
      >
        <ChevronLeft size={18} strokeWidth={1.5} className="text-stone-700" />
      </button>
      <button
        onClick={goNext}
        data-testid="tour-next"
        className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 items-center justify-center w-10 h-10 rounded-full bg-stone-900 text-white shadow-md hover:bg-stone-800"
      >
        <ChevronRight size={18} strokeWidth={1.5} />
      </button>

      {/* Mobile prev/next bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 bg-white/95 backdrop-blur border-t border-stone-200 px-4 py-3 flex items-center justify-between">
        <button onClick={goPrev} disabled={idx === 0} data-testid="tour-prev-mobile" className="px-3 py-2 text-sm text-stone-700 disabled:opacity-30 flex items-center gap-1">
          <ChevronLeft size={14} /> Back
        </button>
        <div className="text-[11px] text-stone-400">{idx + 1} of {SHOTS.length}</div>
        <button onClick={goNext} data-testid="tour-next-mobile" className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm flex items-center gap-1">
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

/* ─── Narrative Panel ─── */
function NarrativePanel({ shot, persona }) {
  return (
    <div className="flex flex-col justify-center" data-testid={`tour-narrative-${shot.id}`}>
      <div className="flex items-center gap-2 mb-6">
        <span className="text-[10px] uppercase tracking-widest text-stone-400">{shot.time}</span>
        <span className="h-px flex-1 bg-stone-200 max-w-16" />
        <span className="text-[10px] uppercase tracking-widest" style={{ color: persona.color }}>{persona.boutiqueLabel}</span>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <Avatar name={persona.name} boutique={persona.boutique} size={48} />
        <div>
          <div className="font-serif-display text-lg text-stone-900">{persona.name}</div>
          <div className="text-xs text-stone-500">{persona.role}</div>
        </div>
      </div>

      <h2 className="font-serif-display text-3xl sm:text-4xl text-stone-900 leading-tight mb-5">{shot.heading}</h2>

      {shot.voice && (
        <blockquote className="border-l-2 pl-4 py-1 mb-5 italic text-stone-700 text-base leading-relaxed" style={{ borderColor: persona.color }}>
          "{shot.voice}"
        </blockquote>
      )}

      <p className="text-sm text-stone-600 leading-relaxed">{shot.caption}</p>
    </div>
  );
}

/* ─── Preview Frame — the little "phone" showing UI ─── */
function PreviewFrame({ shot, persona }) {
  const Preview = shot.Preview;
  return (
    <div className="bg-white rounded-3xl shadow-xl subtle-border overflow-hidden flex flex-col min-h-[420px]" data-testid={`tour-preview-${shot.id}`}>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-100 bg-stone-50/60">
        <div className="flex gap-1.5">
          <span className="w-2.5 h-2.5 rounded-full bg-stone-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-stone-300" />
          <span className="w-2.5 h-2.5 rounded-full bg-stone-300" />
        </div>
        <div className="ml-3 text-[10px] uppercase tracking-widest text-stone-400">
          TOTO · {persona.name.split(" ")[0]}'s View
        </div>
      </div>
      <div className="flex-1 p-5 sm:p-6 overflow-hidden">
        <Preview />
      </div>
    </div>
  );
}

/* ─── Progress Dots ─── */
function ProgressDots({ total, idx, onDot }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="tour-dots">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          onClick={() => onDot(i)}
          data-testid={`tour-dot-${i}`}
          className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-stone-900" : "w-1.5 bg-stone-300 hover:bg-stone-500"}`}
          aria-label={`Go to shot ${i + 1}`}
        />
      ))}
    </div>
  );
}

/* ─── Closing card ─── */
function ClosingCard({ onRestart, onEnter, idx, total }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center text-center px-6" style={{ background: "var(--bg-main, #F7F4EE)" }} data-testid="tour-closing">
      <div className="mb-8">
        <PawMark size={72} />
      </div>
      <h1 className="font-serif-display text-4xl sm:text-6xl text-stone-900 mb-4">TOTO</h1>
      <p className="font-serif-display text-lg sm:text-2xl text-stone-700 italic max-w-2xl mb-8 leading-relaxed">
        One platform. Every persona.<br />One connected day.
      </p>
      <p className="text-sm text-stone-500 max-w-md leading-relaxed mb-12">
        Giving guests memorable experiences while at the same time delivering joy to our team members, vendors, and the community.
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={onRestart}
          data-testid="tour-restart"
          className="px-5 py-2.5 rounded-full subtle-border bg-white text-stone-800 text-sm flex items-center gap-2 hover:bg-stone-50"
        >
          <RotateCcw size={14} /> Watch again
        </button>
        <button
          onClick={onEnter}
          data-testid="tour-enter"
          className="px-5 py-2.5 rounded-full bg-stone-900 text-white text-sm flex items-center gap-2 hover:bg-stone-800"
        >
          Enter TOTO <ArrowRight size={14} />
        </button>
      </div>
      <div className="mt-16 text-[10px] uppercase tracking-widest text-stone-400">
        Shot {total} of {total}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   Preview Panels — stylized snapshots of the real TOTO UI, hard-coded so the
   tour always tells the same story regardless of live data.
   ═══════════════════════════════════════════════════════════════════════════ */

function KatrinaPulsePreview() {
  const boutiques = [
    { code: "PLT", name: "Plantation", color: "#6b8e5a", staff: "8 of 8", rev: "$4,120", trend: 12 },
    { code: "FLL", name: "Fort Lauderdale", color: "#5a7ba0", staff: "6 of 7", rev: "$3,480", trend: -3 },
    { code: "WYN", name: "Wynwood", color: "#c88ba8", staff: "9 of 9", rev: "$5,240", trend: 18 },
    { code: "PNC", name: "Pinecrest", color: "#d89a63", staff: "5 of 6", rev: "$2,760", trend: 4 },
  ];
  return (
    <div>
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-widest text-stone-400">This morning</div>
        <div className="font-serif-display text-xl text-stone-900">District Pulse · Rainbow</div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {boutiques.map((b) => (
          <div key={b.code} className="rounded-2xl subtle-border p-3 bg-white">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-2 h-2 rounded-full" style={{ background: b.color }} />
              <span className="text-xs text-stone-700 font-medium">{b.name}</span>
            </div>
            <div className="text-[11px] text-stone-500">Staff on floor</div>
            <div className="text-sm text-stone-900 mb-2">{b.staff}</div>
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-[11px] text-stone-500">Revenue</div>
                <div className="text-sm text-stone-900">{b.rev}</div>
              </div>
              <div className={`text-[11px] flex items-center gap-0.5 ${b.trend >= 0 ? "text-emerald-600" : "text-rose-500"}`}>
                {b.trend >= 0 ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                {Math.abs(b.trend)}%
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 text-[11px] text-stone-500 italic">FLL is one therapist short today. Tap for coverage options.</div>
    </div>
  );
}

function DamonDailyPreview() {
  const roster = [
    { name: "Ariel Furchess", role: "MT", room: "Room 1", state: "On floor" },
    { name: "Anastacia Young", role: "MT", room: "Room 2", state: "With guest" },
    { name: "Celestine Williams", role: "EG", room: "Front desk", state: "On floor" },
    { name: "Marcus Reid", role: "MT", room: "Room 3", state: "Break" },
  ];
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Tuesday · Feb 16</div>
          <div className="font-serif-display text-xl text-stone-900">Team Plantation</div>
        </div>
        <div className="text-xs text-emerald-700 font-medium">8 of 8 on floor</div>
      </div>

      <div className="rounded-2xl subtle-border p-3 mb-3">
        <div className="text-[11px] uppercase tracking-widest text-stone-400 mb-2">Working today</div>
        <div className="space-y-1.5">
          {roster.map((r) => (
            <div key={r.name} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <Avatar name={r.name} boutique="PLT" size={22} />
                <span className="text-stone-800">{r.name}</span>
                <span className="text-stone-400">· {r.role}</span>
              </div>
              <span className="text-stone-500">{r.state}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl subtle-border p-3">
          <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-stone-400"><DoorOpen size={11} /> Room resets</div>
          <div className="space-y-1 text-xs text-stone-700">
            <div className="flex items-center gap-2"><Check size={12} className="text-emerald-600" /> Room 1 · reset</div>
            <div className="flex items-center gap-2"><Check size={12} className="text-emerald-600" /> Room 2 · reset</div>
            <div className="text-stone-400">Room 3 · pending</div>
          </div>
        </div>
        <div className="rounded-2xl border border-amber-200 bg-amber-50/50 p-3">
          <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-widest text-amber-700"><Hand size={11} /> Gratuities</div>
          <div className="text-xs text-stone-700">2 tips ready to route · <span className="font-medium">$40 · $28</span></div>
          <button className="mt-2 text-[10px] px-2 py-1 rounded-full bg-amber-600 text-white">Log gratuity</button>
        </div>
      </div>
    </div>
  );
}

function AnastaciaHomePreview() {
  return (
    <div>
      <div className="mb-5">
        <div className="text-[10px] uppercase tracking-widest text-stone-400">Good morning</div>
        <div className="font-serif-display text-xl text-stone-900">Anastacia</div>
        <div className="text-[11px] text-stone-500 mt-0.5">PLT · Tuesday shift</div>
      </div>

      <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4 mb-4 shadow-sm" data-testid="tour-gratuity-card">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign size={14} className="text-emerald-700" />
          <span className="text-[11px] uppercase tracking-widest text-emerald-800">New gratuity for you</span>
        </div>
        <div className="font-serif-display text-2xl text-emerald-900 mb-1">$40.00</div>
        <div className="text-xs text-stone-700 mb-3">From 9:15 AM guest · logged by Damon</div>
        <button className="px-3 py-1.5 rounded-full bg-emerald-700 text-white text-xs font-medium">Claim → enjoy</button>
      </div>

      <div className="rounded-2xl subtle-border p-3">
        <div className="text-[11px] uppercase tracking-widest text-stone-400 mb-2">Today</div>
        <div className="space-y-1.5 text-xs text-stone-700">
          <div className="flex items-center gap-2"><Check size={12} className="text-emerald-600" /> AM call button</div>
          <div className="flex items-center gap-2"><Check size={12} className="text-emerald-600" /> Room 2 reset</div>
          <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full border border-stone-300" /> Clean sweep 2</div>
        </div>
      </div>
    </div>
  );
}

function SheilaHiringPreview() {
  const stages = [
    { label: "Interview", tone: "stone", count: 2 },
    { label: "Offer", tone: "emerald", count: 1, highlight: true },
    { label: "Signed", tone: "emerald", count: 3 },
  ];
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Hiring pipeline</div>
          <div className="font-serif-display text-xl text-stone-900">PLT · Massage Therapist</div>
        </div>
        <div className="text-[11px] text-emerald-700 font-medium flex items-center gap-1">
          <Check size={12} /> Signed 11:47 AM
        </div>
      </div>

      {/* Mini kanban */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        {stages.map((s) => (
          <div key={s.label} className={`rounded-xl p-2 border ${s.highlight ? "border-emerald-300 bg-emerald-50" : "border-stone-200 bg-white"}`}>
            <div className="text-[10px] uppercase tracking-widest text-stone-400 mb-1">{s.label}</div>
            <div className="text-lg text-stone-900">{s.count}</div>
          </div>
        ))}
      </div>

      {/* Featured candidate card */}
      <div className="rounded-2xl subtle-border p-4 shadow-sm bg-white">
        <div className="flex items-center gap-3 mb-3">
          <Avatar name="Kira Bennett" boutique="PLT" size={36} />
          <div>
            <div className="text-sm text-stone-900 font-medium">Kira Bennett</div>
            <div className="text-[11px] text-stone-500">MT · Applied Feb 8 · via referral</div>
          </div>
        </div>
        <div className="rounded-lg bg-stone-50 border border-stone-200 p-2 text-[11px] text-stone-600 mb-3 font-mono">
          totos.co/offer/a4f2·1c8e
        </div>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-emerald-700 flex items-center gap-1"><Check size={11} /> Signed by Kira Bennett</span>
          <span className="text-stone-400">Starts Mon Feb 22</span>
        </div>
      </div>
    </div>
  );
}

function RoseBudgetPreview() {
  const categories = [
    { name: "Products", pct: 62, spent: "$3,100", cap: "$5,000", tone: "stone" },
    { name: "Housekeeping", pct: 92, spent: "$920", cap: "$1,000", tone: "amber", flag: true },
    { name: "Marketing", pct: 41, spent: "$820", cap: "$2,000", tone: "stone" },
  ];
  return (
    <div>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Budget · February</div>
          <div className="font-serif-display text-xl text-stone-900">Plantation</div>
        </div>
        <div className="text-[11px] text-stone-500">Mid-month</div>
      </div>

      <div className="space-y-3 mb-4">
        {categories.map((c) => (
          <div key={c.name}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <span className="text-stone-700">{c.name}</span>
              <span className={c.flag ? "text-amber-700 font-medium" : "text-stone-500"}>{c.spent} / {c.cap}</span>
            </div>
            <div className="h-2 rounded-full bg-stone-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${c.pct}%`,
                  background: c.flag ? "#d97706" : "#78716c",
                }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2">
        <AlertTriangle size={14} className="text-amber-700 shrink-0 mt-0.5" />
        <div className="text-[11px] text-amber-900 leading-relaxed">
          <span className="font-medium">Housekeeping at 92% of cap.</span> TOTO pushed a nudge to Damon's Home <span className="italic">2 minutes ago</span>. No email needed.
        </div>
      </div>
    </div>
  );
}
